import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, AlertTriangle, Crown } from "lucide-react";

const MAX_ATTEMPTS = 8;
const POLL_DELAY = 2200;

export default function PremiumSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [state, setState] = useState({ status: "checking", attempts: 0 });
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", error: "Aucune session." });
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      tries.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          // refresh user context
          try {
            const me = await api.get("/auth/me");
            setUser(me.data);
          } catch { /* ignore */ }
          setState({ status: "paid" });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired" });
          return;
        }
        if (tries.current >= MAX_ATTEMPTS) {
          setState({ status: "timeout" });
          return;
        }
        setState({ status: "checking", attempts: tries.current });
        setTimeout(poll, POLL_DELAY);
      } catch (e) {
        setState({ status: "error", error: e.response?.data?.detail || "Vérification impossible" });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, setUser]);

  return (
    <div className="space-y-8 max-w-2xl" data-testid="premium-success-page">
      {state.status === "checking" && (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center" data-testid="premium-checking">
          <Loader2 className="animate-spin mx-auto text-indigo-500" size={36} />
          <p className="font-display text-2xl font-bold text-slate-900 mt-5">Vérification du paiement…</p>
          <p className="text-slate-500 text-sm mt-2">Tentative {tries.current} / {MAX_ATTEMPTS}</p>
        </div>
      )}
      {state.status === "paid" && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-10 text-center" data-testid="premium-paid">
          <div className="w-14 h-14 rounded-2xl bg-emerald-500 text-white flex items-center justify-center mx-auto">
            <CheckCircle2 size={28} />
          </div>
          <p className="font-display text-3xl font-bold text-slate-900 mt-5">Bienvenue dans Premium <Crown size={20} className="inline text-yellow-500" /></p>
          <p className="text-slate-600 mt-2">Ton compte est maintenant Premium pour 1 mois. Profite des modèles et bons plans en avant-première.</p>
          <div className="mt-6 flex justify-center gap-3">
            <Button onClick={() => navigate("/dashboard")} className="bg-slate-900 hover:bg-slate-800" data-testid="go-dashboard-btn">
              Aller au tableau de bord
            </Button>
            <Link to="/premium" className="px-4 py-2 rounded-full border border-slate-200 text-slate-700 text-sm">
              Voir mon abonnement
            </Link>
          </div>
        </div>
      )}
      {(state.status === "expired" || state.status === "timeout" || state.status === "error") && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-10 text-center" data-testid="premium-error">
          <AlertTriangle size={32} className="mx-auto text-red-500" />
          <p className="font-display text-2xl font-bold text-slate-900 mt-4">
            {state.status === "expired" ? "Session expirée" : state.status === "timeout" ? "Vérification trop longue" : "Erreur"}
          </p>
          <p className="text-slate-600 mt-2">{state.error || "Tu peux réessayer le paiement, ou nous contacter si le débit est passé."}</p>
          <div className="mt-6">
            <Button onClick={() => navigate("/premium")} className="bg-slate-900 hover:bg-slate-800">
              Retour à Premium
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
